export type Language = 'en' | 'zh';

const en = {
  appName: 'IssueMint',
  tagline: 'Build GitHub Issue Forms without fighting YAML.',
  undo: 'Undo',
  redo: 'Redo',
  lightTheme: 'Use light theme',
  darkTheme: 'Use dark theme',
  switchLanguage: 'Switch language',
  repository: 'GitHub repository',
  export: 'Export',
  reset: 'Reset',
  ready: 'Ready to export',
  errors: '{count} errors',
  error: '{count} error',
  warnings: '{count} warnings',
  warning: '{count} warning',
  presets: 'Presets',
  bugReport: 'Bug Report',
  featureRequest: 'Feature Request',
  documentationIssue: 'Documentation Issue',
  fields: 'Field library',
  fieldList: 'Form fields',
  markdown: 'Markdown',
  input: 'Text input',
  textarea: 'Textarea',
  dropdown: 'Dropdown',
  checkboxes: 'Checkboxes',
  import: 'Import',
  yamlFile: 'YAML file',
  importHint: 'Choose, drop, or paste a .yml or .yaml file.',
  formSettings: 'Form settings',
  properties: 'Properties',
  yaml: 'YAML',
  validation: 'Validation',
  preview: 'Preview',
  builder: 'Builder',
  filename: 'File name',
  name: 'Name',
  description: 'Description',
  title: 'Default title',
  labels: 'Labels',
  assignees: 'Assignees',
  addValue: 'Type a value and press Enter',
  removeValue: 'Remove {value}',
  fieldType: 'Field type',
  id: 'ID',
  label: 'Label',
  placeholder: 'Placeholder',
  defaultValue: 'Default value',
  render: 'Render language',
  options: 'Options',
  option: 'Option {index}',
  addOption: 'Add option',
  removeOption: 'Remove option {index}',
  multiple: 'Allow multiple selections',
  required: 'Required',
  markdownValue: 'Markdown content',
  selectField: 'Select a field to edit it, or edit the form settings.',
  noFields: 'Add a field from the library to start building.',
  moveUp: 'Move field up',
  moveDown: 'Move field down',
  duplicate: 'Duplicate field',
  delete: 'Delete field',
  expand: 'Expand field',
  collapse: 'Collapse field',
  drag: 'Drag to reorder',
  copied: 'YAML copied',
  copyYaml: 'Copy YAML',
  downloadYaml: 'Download YAML',
  repositorySettings: 'Repository settings',
  blankIssues: 'Allow blank issues',
  contactLinks: 'Contact links',
  contactName: 'Link name',
  contactUrl: 'Link URL',
  contactAbout: 'Link description',
  addContact: 'Add contact link',
  removeContact: 'Remove contact link {index}',
  exportZip: 'Export ZIP',
  cancel: 'Cancel',
  close: 'Close',
  pasteYaml: 'Paste YAML',
  chooseFile: 'Choose file',
  importYaml: 'Import YAML',
  dropHere: 'Drop YAML here',
  validExample: 'Valid example',
  importFailed: 'Import failed',
  importSuccess: 'YAML imported',
  zipExported: 'Repository template ZIP exported',
  downloadStarted: 'YAML download started',
  presetChanged: 'Preset loaded',
  addedField: '{type} field added',
  formPreview: 'GitHub-style live preview',
  submitIssue: 'Submit new issue',
  selectOption: 'Select an option',
  privateNotice: 'Everything stays in this browser.',
  validationEmpty: 'No validation issues found.',
  formIssue: 'Form',
  selectedField: 'Selected field',
  presetConfirm: 'Replace your unsaved changes with this preset?',
  deleteConfirm: 'Delete this field?',
  resetConfirm: 'Reset the editor and clear locally saved data?',
  resetDone: 'Editor reset',
  unsafeExport: 'Fix all errors before exporting.',
  invalidFileType: 'Choose a .yml or .yaml file.',
  fileTooLarge: 'The YAML file is larger than 512 KB.',
  unknownField: 'Unsupported field',
  on: 'On',
  off: 'Off',
} as const;

const zh: Record<keyof typeof en, string> = {
  appName: 'IssueMint',
  tagline: '告别 YAML 拉扯，直观构建 GitHub Issue Forms。',
  undo: '撤销',
  redo: '重做',
  lightTheme: '使用浅色主题',
  darkTheme: '使用深色主题',
  switchLanguage: '切换语言',
  repository: 'GitHub 仓库',
  export: '导出',
  reset: '重置',
  ready: '可以导出',
  errors: '{count} 个错误',
  error: '{count} 个错误',
  warnings: '{count} 个警告',
  warning: '{count} 个警告',
  presets: '预设模板',
  bugReport: '错误报告',
  featureRequest: '功能建议',
  documentationIssue: '文档问题',
  fields: '字段组件库',
  fieldList: '表单字段',
  markdown: 'Markdown',
  input: '单行输入',
  textarea: '多行文本',
  dropdown: '下拉选项',
  checkboxes: '复选框',
  import: '导入',
  yamlFile: 'YAML 文件',
  importHint: '选择、拖放或粘贴 .yml/.yaml 文件。',
  formSettings: '表单设置',
  properties: '属性',
  yaml: 'YAML',
  validation: '校验',
  preview: '预览',
  builder: '构建',
  filename: '文件名',
  name: '名称',
  description: '说明',
  title: '默认标题',
  labels: '标签',
  assignees: '负责人',
  addValue: '输入内容后按 Enter',
  removeValue: '删除 {value}',
  fieldType: '字段类型',
  id: 'ID',
  label: '标签文字',
  placeholder: '占位提示',
  defaultValue: '默认值',
  render: '渲染语言',
  options: '选项',
  option: '选项 {index}',
  addOption: '添加选项',
  removeOption: '删除选项 {index}',
  multiple: '允许多选',
  required: '必填',
  markdownValue: 'Markdown 内容',
  selectField: '选择字段进行编辑，也可以编辑表单设置。',
  noFields: '从组件库添加字段以开始构建。',
  moveUp: '字段上移',
  moveDown: '字段下移',
  duplicate: '复制字段',
  delete: '删除字段',
  expand: '展开字段',
  collapse: '折叠字段',
  drag: '拖拽排序',
  copied: '已复制 YAML',
  copyYaml: '复制 YAML',
  downloadYaml: '下载 YAML',
  repositorySettings: '仓库设置',
  blankIssues: '允许空白 Issue',
  contactLinks: '联系链接',
  contactName: '链接名称',
  contactUrl: '链接地址',
  contactAbout: '链接说明',
  addContact: '添加联系链接',
  removeContact: '删除联系链接 {index}',
  exportZip: '导出 ZIP',
  cancel: '取消',
  close: '关闭',
  pasteYaml: '粘贴 YAML',
  chooseFile: '选择文件',
  importYaml: '导入 YAML',
  dropHere: '将 YAML 拖到这里',
  validExample: '合法示例',
  importFailed: '导入失败',
  importSuccess: '已导入 YAML',
  zipExported: '已导出仓库模板 ZIP',
  downloadStarted: '已开始下载 YAML',
  presetChanged: '已加载预设',
  addedField: '已添加 {type} 字段',
  formPreview: 'GitHub 风格实时预览',
  submitIssue: '提交新 Issue',
  selectOption: '请选择',
  privateNotice: '所有内容只保留在此浏览器中。',
  validationEmpty: '未发现校验问题。',
  formIssue: '表单',
  selectedField: '当前字段',
  presetConfirm: '用此预设覆盖当前未保存的修改吗？',
  deleteConfirm: '确定删除此字段吗？',
  resetConfirm: '重置编辑器并清除本地保存的数据吗？',
  resetDone: '编辑器已重置',
  unsafeExport: '请先修复所有错误。',
  invalidFileType: '请选择 .yml 或 .yaml 文件。',
  fileTooLarge: 'YAML 文件不能超过 512 KB。',
  unknownField: '不支持的字段',
  on: '开启',
  off: '关闭',
};

export type TranslationKey = keyof typeof en;

const dictionaries: Record<Language, Record<TranslationKey, string>> = { en, zh };

export function translate(
  language: Language,
  key: TranslationKey,
  values: Record<string, string | number> = {},
): string {
  return Object.entries(values).reduce(
    (result, [name, value]) => result.replaceAll(`{${name}}`, String(value)),
    dictionaries[language][key],
  );
}
